import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { functionError } from '@/hooks/useMix'
import type { RecordsState, RecordFind, RecordTypeId } from '@/components/alerts/types'

/**
 * Public records (docs/PUBLIC-RECORDS.md v0.3; records.html v1.1,
 * settings-records.html v1.1): the record types and sources, the records
 * budget, and the finds. Writes go through security definer functions
 * (migration 20240037); the checks run in the records-check function.
 */

const rpc = async <T>(fn: string, args?: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.rpc(fn as never, args as never)
  if (error) throw error
  return data as T
}

export function useRecordsState() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['records-state', clientId],
    enabled: !!clientId,
    staleTime: 15_000,
    queryFn: () => rpc<RecordsState>('records_state'),
  })
}

export function useRecordFinds() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['record-finds', clientId],
    enabled: !!clientId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('record_finds' as never).select('*')
        .eq('client_id', clientId!).order('created_at', { ascending: false }).limit(300)
      if (error) throw error
      return (data ?? []) as unknown as RecordFind[]
    },
  })
}

function useInvalidate() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['records-state', clientId] })
    qc.invalidateQueries({ queryKey: ['record-finds', clientId] })
    qc.invalidateQueries({ queryKey: ['seed-balance', clientId] })
  }
}

export function useSetRecords() {
  const done = useInvalidate()
  return useMutation({ mutationFn: (patch: { paused?: boolean; push?: boolean }) => rpc('records_set', { p_patch: patch }), onSuccess: done })
}

/** The free area search: finds the sources for the account's places. */
export function useAreaSearch() {
  const done = useInvalidate()
  return useMutation({
    mutationFn: async (types?: RecordTypeId[]) => {
      const { data, error } = await supabase.functions.invoke('records-area-search', { body: { types } })
      if (error) throw await functionError(error)
      return data as { added: number; searched: number }
    },
    onSuccess: done,
  })
}

/** A type on or off. Turning one on with no sources yet runs the area search (free). */
export function useSetRecordType() {
  const done = useInvalidate()
  const search = useAreaSearch()
  return useMutation({
    mutationFn: async (a: { type: RecordTypeId; on: boolean }) => {
      const needSearch = await rpc<boolean>('records_type_set', { p_type: a.type, p_on: a.on })
      done()
      if (needSearch) await search.mutateAsync([a.type])
      return needSearch
    },
    onSuccess: done,
  })
}

export function useSetRecordSource() {
  const done = useInvalidate()
  return useMutation({ mutationFn: (a: { id: string; on: boolean }) => rpc('records_source_set', { p_id: a.id, p_on: a.on }), onSuccess: done })
}

export function useDismissFind() {
  const done = useInvalidate()
  return useMutation({ mutationFn: (id: string) => rpc('find_dismiss', { p_id: id }), onSuccess: done })
}

/** Build, step one: the find becomes a headline marked Public record; the
 *  caller then runs the feed's build (useBuildStory). */
export async function findHeadline(id: string): Promise<string> {
  return rpc<string>('find_headline', { p_id: id })
}

/** Brian's "Run the records check" test: the weekly check, now. Admins, or the local stack. */
export function useRunRecordsCheck() {
  const done = useInvalidate()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('records-check', { body: {} })
      if (error) throw await functionError(error)
      return data as { checked?: number; unreachable?: number; skipped?: number; found?: number; charged?: number; why?: string }
    },
    onSuccess: done,
  })
}

const plural = (n: number, one: string) => `${n} ${n === 1 ? one : one + 's'}`

/**
 * Feed controls' one-line summary for Public records (Brian's feed-controls.js):
 *   "6 sources checked weekly" · "Off · turn on permits, licenses, agendas"
 */
export function useRecordsSummary(): string | null {
  const { data: r } = useRecordsState()
  if (!r) return null
  return r.on ? `${plural(r.on, 'source')} checked weekly` : 'Off · turn on permits, licenses, agendas'
}
